import {
  useCallback, useMemo,
} from 'react';
import { flatten } from '~/_shared/utils/collections/collections';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { memoizeOne } from '~/_shared/utils/memoize/memoize';
import { sortAlphabetically } from '~/_shared/utils/sort';
import {
  type BoundaryGroup, type BoundaryGroupBase,
} from '~/boundary/boundary.types';
import { translate } from '~/translations/Trans';
import { type AppState } from '../app.store';
import type { BoundaryGroupId } from '../boundaries/boundaryIdentifier.type';
import { clientMembersSelector } from '../userData/userData.selectors';
import type {
  BoundaryGroupDemographic, DemographicIdCode,
} from './boundaryGroups.repository';

export const boundaryGroupsSelector = (state: AppState) => state.boundaries.groups.groups;
export const useBoundaryGroupsSelector = () => useSelector(boundaryGroupsSelector);

const listedBoundaryGroupsSelectorMemoized = memoizeOne((groups: ReadonlyArray<BoundaryGroup>) => groups.filter(g => g.visible));
export const listedBoundaryGroupsSelector = (state: AppState) => listedBoundaryGroupsSelectorMemoized(boundaryGroupsSelector(state));
export const useListedBoundaryGroupsSelector = () => useSelector(listedBoundaryGroupsSelector);

export const boundaryGroupsIsLoadingSelector = (state: AppState) => state.boundaries.groups.isLoading;
export const useBoundaryGroupsIsLoadingSelector = () => useSelector(boundaryGroupsIsLoadingSelector);

const boundaryGroupsIsListingLoadingSelector = (state: AppState) => state.boundaries.groups.isListingLoading;
export const useBoundaryGroupsIsListingLoadingSelector = () => useSelector(boundaryGroupsIsListingLoadingSelector);

export const useGlobalBoundaryGroups = () => {
  const boundaryGroups = useListedBoundaryGroupsSelector();

  return useMemo(() => {
    return boundaryGroups.filter(group => group.clientId === null);
  }, [boundaryGroups]);
};

export const boundaryGroupSelector = createStateSelector([
  boundaryGroupsSelector,
  (_state, boundaryGroupId: Maybe<BoundaryGroupId>) => boundaryGroupId,
], (boundaryGroups, boundaryGroupId) => {
  return boundaryGroups.find((group) => group.id === boundaryGroupId);
});

export const useBoundaryGroupSelector = (boundaryGroupId: Maybe<BoundaryGroupId>) => {
  return useSelector((state) => boundaryGroupSelector(state, boundaryGroupId));
};

export const checkBoundaryIsCustom = (group: Maybe<BoundaryGroupBase>) => {
  return !!group && group.clientId !== null;
};

export const boundaryGroupIsCustomSelector = createStateSelector(
  boundaryGroupsSelector,
  (_state, boundaryGroupId: Maybe<BoundaryGroupId>) => boundaryGroupId,
  (boundaryGroups, boundaryGroupId: Maybe<BoundaryGroupId>) =>
    checkBoundaryIsCustom(findBoundaryGroup(boundaryGroups, boundaryGroupId))
);

export const useCheckBoundaryIsCustom = () => {
  const { getBoundaryGroupById } = useGetBoundaryGroup();

  const checkCb = useCallback((boundaryGroupId: Maybe<BoundaryGroupId>) => {
    const boundaryGroup = getBoundaryGroupById(boundaryGroupId);

    if (!boundaryGroup) {
      return false;
    }

    return checkBoundaryIsCustom(boundaryGroup);
  }, [getBoundaryGroupById]);

  return {
    checkBoundaryIsCustom: checkCb,
  };
};

export const useBoundaryIsCustom = (boundaryGroupId: Maybe<BoundaryGroupId>) => {
  const { checkBoundaryIsCustom } = useCheckBoundaryIsCustom();

  return useMemo(() => checkBoundaryIsCustom(boundaryGroupId),
    [boundaryGroupId, checkBoundaryIsCustom]);
};

export const getBoundaryGroupSelector = createStateSelector([boundaryGroupsSelector], (boundaryGroups) => {
  const getBoundaryGroupById = (boundaryGroupId: Maybe<BoundaryGroupId>) =>
    findBoundaryGroup(boundaryGroups, boundaryGroupId);

  return {
    getBoundaryGroupById,
  };
});

export const useGetBoundaryGroup = () => useSelector(getBoundaryGroupSelector);

export const checkBoundaryGroupPermissionsSelector = createStateSelector([
  getBoundaryGroupSelector,
  clientMembersSelector,
],
({ getBoundaryGroupById }, clientMembers) => {
  const checkBoundaryGroupPermissions = (boundaryGroupId: Maybe<BoundaryGroupId>) => {
    const boundaryGroup = getBoundaryGroupById(boundaryGroupId);

    const boundaryIsCustom = checkBoundaryIsCustom(boundaryGroup);
    const boundaryOwnerId = boundaryGroup?.userId ?? null;
    const boundaryIsEditable = boundaryGroup?.allowEditing ?? false;

    const boundaryOwner = boundaryOwnerId
      ? { boundaryOwnerId, boundaryOwnerName: clientMembers.find((member) => member.id === boundaryOwnerId)?.name ?? translate('Unknown') }
      : { boundaryOwnerId: null, boundaryOwnerName: null };

    return {
      boundaryIsCustom,
      boundaryIsEditable,
      ...boundaryOwner,
    };
  };

  return { checkBoundaryGroupPermissions };
});

export const useCheckBoundaryGroupPermissions = () => {
  return useSelector(checkBoundaryGroupPermissionsSelector);
};

export const useBoundaryGroupPermissions = (boundaryGroupId: Maybe<BoundaryGroupId>) => {
  const { checkBoundaryGroupPermissions } = useCheckBoundaryGroupPermissions();

  return useMemo(() => checkBoundaryGroupPermissions(boundaryGroupId),
    [boundaryGroupId, checkBoundaryGroupPermissions]);
};

export type BoundaryGroupPermissions = ReturnType<typeof useBoundaryGroupPermissions>;

export const useCustomBoundaryGroupsSelector = () => {
  const boundaryGroups = useListedBoundaryGroupsSelector();

  return useMemo(() => {
    return boundaryGroups
      .filter(group => checkBoundaryIsCustom(group))
      .sort((a, b) => sortAlphabetically(a.name, b.name));
  }, [boundaryGroups]);
};

export const selectAllDemographicsMap = createStateSelector([
  boundaryGroupsSelector,
], (boundaryGroups) => {
  const entries = flatten(boundaryGroups.map((group) => {
    if (group.demographics) {
      return group.demographics.map(demographic => (
        [demographic.id, demographic] as const
      ));
    }
    return [];
  }));
  return new Map(entries) as ReadonlyMap<DemographicIdCode, BoundaryGroupDemographic>;
});

export const useSelectAllDemographicsMap = () => useSelector(selectAllDemographicsMap);

// #region Get Boundary Group by ID

export const findBoundaryGroup = (boundaryGroups: readonly BoundaryGroup[], boundaryGroupId: Maybe<BoundaryGroupId>) =>
  boundaryGroups.find(boundaryGroup => boundaryGroup.id === boundaryGroupId);

// #endregion
